import capitalize from 'lodash/capitalize';
import { useMemo, useState } from 'react';

import { AffiliationLevel, GUID, InternalUserProfile } from '@ndvr/auth-api';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useInternalUserDisplayName } from '@/modules/internal-user-queries';
import { toNumber } from 'utils';
import { useCommandCenterHouseholds } from 'views/household/household-queries';

import {
    AffiliationFilter,
    HouseholdFilterKeys,
    HouseholdFilters,
    useHouseholdFiltersContext,
} from './HouseholdFiltersContext';
import { AFFILIATION_LEVEL_EXTERNAL_VALUE } from './HouseholdFiltersHook';

type FilterType = 'advisor' | 'affiliation' | 'aum' | 'dates';

const affiliationOptions = [
    {
        value: AFFILIATION_LEVEL_EXTERNAL_VALUE,
        label: capitalize(AFFILIATION_LEVEL_EXTERNAL_VALUE),
    },
    ...Object.values(AffiliationLevel).map((affiliationLevel) => ({
        value: affiliationLevel,
        label: capitalize(affiliationLevel),
    })),
];

export const HouseholdMultiFilter = () => {
    const {
        filters,
        patchFilters,
        resetFilters,
        primaryAdvisorFilterCount,
        affiliationLevelFilterCount,
        aumFilterCount,
        datesFilterCount,
    } = useHouseholdFiltersContext();
    const sections: MultiFilterSection<FilterType>[] = [
        {
            id: 'advisor',
            label: 'Advisor',
            count: primaryAdvisorFilterCount,
            onClear: () => resetFilters([HouseholdFilterKeys.primaryAdvisorGUIDs]),
            content: <AdvisorFilterContent filters={filters} patchFilters={patchFilters} />,
        },
        {
            id: 'affiliation',
            label: 'Type',
            count: affiliationLevelFilterCount,
            onClear: () => resetFilters([HouseholdFilterKeys.affiliationLevels]),
            content: <AffiliationFilterContent filters={filters} patchFilters={patchFilters} />,
        },
        {
            id: 'aum',
            label: 'AUM',
            count: aumFilterCount,
            onClear: () =>
                resetFilters([
                    HouseholdFilterKeys.hasMoneyInvested,
                    HouseholdFilterKeys.isHNW,
                    HouseholdFilterKeys.minAUM,
                    HouseholdFilterKeys.maxAUM,
                ]),
            content: <AUMFilterContent filters={filters} patchFilters={patchFilters} />,
        },
        {
            id: 'dates',
            label: 'Key Dates',
            count: datesFilterCount,
            onClear: () =>
                resetFilters([
                    HouseholdFilterKeys.createdAtStart,
                    HouseholdFilterKeys.createdAtEnd,
                    HouseholdFilterKeys.agreementSignedStart,
                    HouseholdFilterKeys.agreementSignedEnd,
                ]),
            content: <DatesFilterContent filters={filters} patchFilters={patchFilters} />,
        },
    ];

    return (
        <MultiFilter
            sections={sections}
            defaultSectionId="advisor"
            dropdownClassName="h-96 w-[440px] p-0"
            contentClassName="min-h-0 flex-1 overflow-y-auto p-4 pt-3"
        />
    );
};

const AdvisorFilterContent = ({
    filters,
    patchFilters,
}: {
    filters: HouseholdFilters;
    patchFilters: (partialFilters: Partial<HouseholdFilters>) => void;
}) => {
    const { data: households = [] } = useCommandCenterHouseholds();
    const getInternalUserName = useInternalUserDisplayName();
    const [searchTerm, setSearchTerm] = useState('');
    const advisorOptions = useMemo(
        () =>
            [...new Set(households.map((household) => household.primaryAdvisorGUID))]
                .filter((guid): guid is GUID<InternalUserProfile> => guid != null)
                .map((guid) => ({ guid, name: getInternalUserName(guid) }))
                .filter((advisor) => advisor.name && advisor.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [households, getInternalUserName, searchTerm],
    );

    const toggleAdvisor = (advisorGuid: GUID<InternalUserProfile>) => {
        const current = filters.primaryAdvisorGUIDs;
        patchFilters({
            [HouseholdFilterKeys.primaryAdvisorGUIDs]: current.includes(advisorGuid)
                ? current.filter((guid) => guid !== advisorGuid)
                : [...current, advisorGuid],
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <Input
                placeholder="Search advisors..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
            />
            <div className="space-y-2">
                {advisorOptions.length === 0 ? (
                    <div className="py-4 text-center text-sm text-gray-500">No advisors found</div>
                ) : (
                    advisorOptions.map((advisor) => (
                        <FilterOptionRow
                            key={advisor.guid}
                            checked={filters.primaryAdvisorGUIDs.includes(advisor.guid)}
                            label={advisor.name}
                            onToggle={() => toggleAdvisor(advisor.guid)}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

const AffiliationFilterContent = ({
    filters,
    patchFilters,
}: {
    filters: HouseholdFilters;
    patchFilters: (partialFilters: Partial<HouseholdFilters>) => void;
}) => {
    const toggleAffiliation = (affiliationLevel: AffiliationFilter) => {
        const current = filters.affiliationLevels;
        patchFilters({
            [HouseholdFilterKeys.affiliationLevels]: current.includes(affiliationLevel)
                ? current.filter((level) => level !== affiliationLevel)
                : [...current, affiliationLevel],
        });
    };

    return (
        <div className="space-y-2">
            {affiliationOptions.map((option) => (
                <FilterOptionRow
                    key={option.value}
                    checked={filters.affiliationLevels.includes(option.value as AffiliationFilter)}
                    label={option.label}
                    onToggle={() => toggleAffiliation(option.value as AffiliationFilter)}
                />
            ))}
        </div>
    );
};

const AUMFilterContent = ({
    filters,
    patchFilters,
}: {
    filters: HouseholdFilters;
    patchFilters: (partialFilters: Partial<HouseholdFilters>) => void;
}) => {
    const patchAUM = (partialFilters: Partial<HouseholdFilters>) => {
        const hasNewRangeValue = [partialFilters.minAUM, partialFilters.maxAUM].some(
            (value) => value !== null && value !== undefined,
        );

        patchFilters({
            ...(hasNewRangeValue ? { [HouseholdFilterKeys.hasMoneyInvested]: true } : {}),
            ...partialFilters,
        });
    };

    return (
        <div className="space-y-4">
            <FilterOptionRow
                checked={filters.hasMoneyInvested}
                label="Has Money Invested"
                onToggle={() =>
                    patchFilters({
                        [HouseholdFilterKeys.hasMoneyInvested]: !filters.hasMoneyInvested,
                    })
                }
            />
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Min</Label>
                    <Input
                        type="number"
                        min={0}
                        placeholder="$ Min"
                        value={filters.minAUM ?? ''}
                        onChange={(event) =>
                            patchAUM({ [HouseholdFilterKeys.minAUM]: toNumber(event.target.value) ?? null })
                        }
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Max</Label>
                    <Input
                        type="number"
                        min={0}
                        placeholder="$ Max"
                        value={filters.maxAUM ?? ''}
                        onChange={(event) =>
                            patchAUM({ [HouseholdFilterKeys.maxAUM]: toNumber(event.target.value) ?? null })
                        }
                    />
                </div>
            </div>
        </div>
    );
};

const DatesFilterContent = ({
    filters,
    patchFilters,
}: {
    filters: HouseholdFilters;
    patchFilters: (partialFilters: Partial<HouseholdFilters>) => void;
}) => {
    return (
        <div className="space-y-4">
            <DateRangeFields
                title="Household Created"
                startValue={filters.createdAtStart}
                endValue={filters.createdAtEnd}
                onStartChange={(value) => patchFilters({ [HouseholdFilterKeys.createdAtStart]: value })}
                onEndChange={(value) => patchFilters({ [HouseholdFilterKeys.createdAtEnd]: value })}
            />
            <DateRangeFields
                title="Client Agreement Signed"
                startValue={filters.agreementSignedStart}
                endValue={filters.agreementSignedEnd}
                onStartChange={(value) => patchFilters({ [HouseholdFilterKeys.agreementSignedStart]: value })}
                onEndChange={(value) => patchFilters({ [HouseholdFilterKeys.agreementSignedEnd]: value })}
            />
        </div>
    );
};

const DateRangeFields = ({
    title,
    startValue,
    endValue,
    onStartChange,
    onEndChange,
}: {
    title: string;
    startValue: string | null;
    endValue: string | null;
    onStartChange: (value: string | null) => void;
    onEndChange: (value: string | null) => void;
}) => (
    <div className="space-y-2">
        <Label className="text-sm font-medium">{title}</Label>
        <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
                <Label className="text-xs text-gray-600">Start</Label>
                <Input
                    type="date"
                    value={startValue ?? ''}
                    onChange={(event) => onStartChange(event.target.value || null)}
                />
            </div>
            <div className="space-y-1">
                <Label className="text-xs text-gray-600">End</Label>
                <Input
                    type="date"
                    value={endValue ?? ''}
                    onChange={(event) => onEndChange(event.target.value || null)}
                />
            </div>
        </div>
    </div>
);

const FilterOptionRow = ({ checked, label, onToggle }: { checked: boolean; label: string; onToggle: () => void }) => (
    <div
        role="button"
        tabIndex={0}
        className="flex w-full cursor-pointer items-center gap-3 rounded p-2 text-left hover:bg-gray-50"
        onClick={onToggle}
        onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggle();
            }
        }}
    >
        <Checkbox
            checked={checked}
            onClick={(event) => {
                event.stopPropagation();
            }}
            onCheckedChange={onToggle}
        />
        <span className="text-sm">{label}</span>
    </div>
);
